'use client';

import { type RefObject, useEffect, useRef, useState } from 'react';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// Fraction of the whole scroll range, not per-tile distance — reaches the last index in a two-up
// layout and matches the snap target so the dot never desyncs from the snapped position.
const indexFromScroll = (scroller: HTMLElement, itemCount: number) => {
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const fraction = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
    return Math.round(fraction * (itemCount - 1));
};

const scrollToIndex = (scroller: HTMLElement, index: number, itemCount: number) => {
    if (itemCount < 2) {
        return;
    }
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const target = (index / (itemCount - 1)) * maxScroll;
    // Already aligned — bail so the snap can't re-fire on its own settle.
    if (Math.abs(target - scroller.scrollLeft) < 1) {
        return;
    }
    scroller.scrollTo({ left: target, behavior: 'smooth' });
};

type CarouselSnap = {
    activeIndex: number;
    canPaginate: boolean;
    goToIndex: (index: number) => void;
};

// `rootRef` must wrap the DraggableContainer; the scroller is its `.draggable-wrapper`. Snapping fires
// on `scrollend` — only once momentum has fully settled — so a swipe is never interrupted mid-fling.
export const useCarouselSnap = (rootRef: RefObject<HTMLElement | null>, itemCount: number): CarouselSnap => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [canPaginate, setCanPaginate] = useState(false);
    const scrollerRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const scroller = rootRef.current?.querySelector<HTMLElement>('.draggable-wrapper') ?? null;
        scrollerRef.current = scroller;
        if (!scroller || itemCount < 2) {
            return undefined;
        }

        const updateActive = () => {
            setActiveIndex(indexFromScroll(scroller, itemCount));
            // >1px overflow (sub-pixel rounding tolerance) means the row actually scrolls
            setCanPaginate(scroller.scrollWidth - scroller.clientWidth > 1);
        };
        const onScrollEnd = () => scrollToIndex(scroller, indexFromScroll(scroller, itemCount), itemCount);

        updateActive();
        scroller.addEventListener('scroll', updateActive, { passive: true });
        scroller.addEventListener('scrollend', onScrollEnd);
        window.addEventListener('resize', updateActive);
        return () => {
            scroller.removeEventListener('scroll', updateActive);
            scroller.removeEventListener('scrollend', onScrollEnd);
            window.removeEventListener('resize', updateActive);
        };
    }, [rootRef, itemCount]);

    const goToIndex = (index: number) => {
        const scroller = scrollerRef.current;
        if (scroller) {
            scrollToIndex(scroller, clamp(index, 0, itemCount - 1), itemCount);
        }
    };

    return { activeIndex, canPaginate, goToIndex };
};
